import React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import TableSortLabel from '@mui/material/TableSortLabel';

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label='first page'>
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='next page'
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label='last page'
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

const TableWithPagination = (props) => {
  const {
    headers,
    sortableHeaders,
    objects,
    keys,
    formatting,
    perPage = 10,
    isMobile = false,
    serverPagination = null, // { onPageChange, onRowsPerPageChange, onSortChange, totalCount, currentPage, currentRowsPerPage, currentSortBy, currentSortOrder }
  } = props;

  const isServerPaginated = serverPagination !== null;

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(perPage);
  const [orderBy, setOrderBy] = React.useState(null);
  const [order, setOrder] = React.useState('asc');

  // Use server pagination values if available
  const currentPage = isServerPaginated ? serverPagination.currentPage : page;
  const currentRowsPerPage = isServerPaginated ? serverPagination.currentRowsPerPage : rowsPerPage;
  const totalCount = isServerPaginated ? serverPagination.totalCount : objects.length;
  const currentOrderBy = isServerPaginated && serverPagination.currentSortBy ? serverPagination.currentSortBy : orderBy;
  const currentOrder =
    isServerPaginated && serverPagination.currentSortOrder ? serverPagination.currentSortOrder : order;

  const handleRequestSort = (property) => {
    const isAsc = currentOrderBy === property && currentOrder === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';

    if (isServerPaginated && serverPagination.onSortChange) {
      serverPagination.onSortChange(property, newOrder);
    } else {
      setOrder(newOrder);
      setOrderBy(property);
    }
  };

  const sortedObjects = React.useMemo(() => {
    // Skip client-side sorting if using server pagination
    if (isServerPaginated || !currentOrderBy) return objects;

    return [...objects].sort((a, b) => {
      const aValue =
        formatting && formatting[currentOrderBy] ? formatting[currentOrderBy](a[currentOrderBy]) : a[currentOrderBy];
      const bValue =
        formatting && formatting[currentOrderBy] ? formatting[currentOrderBy](b[currentOrderBy]) : b[currentOrderBy];

      // Handle the new formatting structure that includes display and sortValue
      const aSortValue = aValue?.sortValue !== undefined ? aValue.sortValue : aValue;
      const bSortValue = bValue?.sortValue !== undefined ? bValue.sortValue : bValue;

      // Check if values are numeric
      const aNum = Number(aSortValue);
      const bNum = Number(bSortValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum);

      if (isNumeric) {
        if (currentOrder === 'asc') {
          return aNum - bNum;
        } else {
          return bNum - aNum;
        }
      } else {
        // String comparison
        const aStr = String(aSortValue).toLowerCase();
        const bStr = String(bSortValue).toLowerCase();
        if (currentOrder === 'asc') {
          return aStr.localeCompare(bStr);
        } else {
          return bStr.localeCompare(aStr);
        }
      }
    });
  }, [objects, currentOrderBy, currentOrder, formatting, isServerPaginated]);

  // Avoid a layout jump when reaching the last page with empty rows.
  // For server pagination, calculate based on current page items vs rowsPerPage
  // For client pagination, calculate based on total items
  const emptyRows = isServerPaginated
    ? Math.max(0, currentRowsPerPage - objects.length)
    : currentPage > 0
      ? Math.max(0, (1 + currentPage) * currentRowsPerPage - totalCount)
      : 0;

  const handleChangePage = (event, newPage) => {
    if (isServerPaginated) {
      serverPagination.onPageChange(newPage);
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (isServerPaginated) {
      serverPagination.onRowsPerPageChange(newRowsPerPage);
    } else {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  return (
    <TableContainer component={Paper} sx={{ marginTop: 1, marginBottom: 12.5, minWidth: isMobile ? '' : 725 }}>
      <Table sx={{ minWidth: 500 }} aria-label='table with pagination'>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell key={index} sortDirection={currentOrderBy === keys[index] ? currentOrder : false}>
                {sortableHeaders.includes(header) ? (
                  <TableSortLabel
                    active={currentOrderBy === keys[index]}
                    direction={currentOrderBy === keys[index] ? currentOrder : 'asc'}
                    onClick={() => handleRequestSort(keys[index])}
                    sx={{ fontFamily: 'HK Grotesk', fontWeight: 600, fontSize: isMobile ? 12 : 14 }}
                  >
                    {header}
                  </TableSortLabel>
                ) : (
                  <span style={{ fontFamily: 'HK Grotesk', fontWeight: 600, fontSize: isMobile ? 12 : 14 }}>
                    {header}
                  </span>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(isServerPaginated
            ? sortedObjects
            : currentRowsPerPage > 0
              ? sortedObjects.slice(
                  currentPage * currentRowsPerPage,
                  currentPage * currentRowsPerPage + currentRowsPerPage
                )
              : sortedObjects
          ).map((row, index) => (
            <TableRow key={index}>
              {keys.map((key, index) => {
                const formattedValue = formatting && formatting[key] ? formatting[key](row[key]) : row[key];
                return (
                  <TableCell
                    key={index}
                    component='th'
                    scope='row'
                    sx={{ fontFamily: 'HK Grotesk', fontSize: isMobile ? 12 : 14 }}
                  >
                    {formattedValue?.display || formattedValue}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={headers.length} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={headers.length}
              count={totalCount}
              rowsPerPage={currentRowsPerPage}
              page={currentPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                },
              }}
              sx={{
                '.css-pdct74-MuiTablePagination-selectLabel': {
                  fontFamily: 'HK Grotesk',
                  fontSize: isMobile ? 12 : 14,
                },
                '.css-m3l1ig-MuiNativeSelect-select-MuiInputBase-input': {
                  fontFamily: 'HK Grotesk',
                  fontSize: isMobile ? 12 : 14,
                },
                '.css-levciy-MuiTablePagination-displayedRows': {
                  fontFamily: 'HK Grotesk',
                  fontSize: isMobile ? 12 : 14,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default TableWithPagination;
